import { useState, type ComponentProps } from "react";
import { format, parseISO } from "date-fns";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import { InlineDateField } from "@/components/waste/inline-date-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { dateFnsLocale } from "@/lib/format";
import { EARLIEST_DAY, EARLIEST_YEAR, isBeforeEarliest, parseDay } from "@/lib/date-input";
import { cn } from "@/lib/utils";
import { useWasteFilters, type DatePreset, type WasteFilters } from "@/hooks/use-waste-filters";
import { weekEndDay, type DayRange } from "@/lib/zoned-day";

/**
 * Homepage period control (POS2-865): This week / Last week / Custom segments plus a
 * date label. Writes the same URL-backed `useWasteFilters` state the stats/list
 * queries already consume — Custom opens a start/end range committed via
 * `setCustomRange`, exactly like the FilterPopover's date fields.
 */
export function PeriodControl({
  defaultPreset,
  defaultSiteId,
}: {
  defaultPreset: DatePreset;
  /** The page's default site — must match what the page passes its own useWasteFilters.
   * It feeds the timezone entity, so an instance without it resolves the org's zone and
   * the week label can name a different week than the data (POS2-1039). */
  defaultSiteId?: string | null;
}) {
  const { locale, t } = useTranslation();
  const { filters, dayRange, setPreset, setCustomRange } = useWasteFilters(
    defaultPreset,
    defaultSiteId,
  );
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  function openCustom(open: boolean) {
    if (open) {
      // Seed the pickers with the applied custom range so reopening shows it.
      setFrom(filters.preset === "custom" ? (dayRange.from ?? "") : "");
      setTo(filters.preset === "custom" ? (dayRange.to ?? "") : "");
    }
    setCustomOpen(open);
  }

  // Both sides are canonical "yyyy-MM-dd" here, so they compare lexicographically. An end
  // before the start matches nothing, making it a typo rather than a query — block Apply and
  // say why, as the transactions date-range popover already does (POS2-1068).
  const invertedRange = from !== "" && to !== "" && to < from;
  // A year the estate has no data for is a typo too, and one that reads as an answer rather than
  // an error: a 1940 start makes ~31,300 days, which collapses the weekly-spend target to nothing
  // (POS2-1279). Same treatment as the inversion — refuse at Apply and say why, never clamp.
  const tooEarly = isBeforeEarliest(from) || isBeforeEarliest(to);

  function applyCustom() {
    setCustomRange(
      from ? new Date(`${from}T00:00:00.000`).toISOString() : null,
      to ? new Date(`${to}T23:59:59.999`).toISOString() : null,
    );
    setCustomOpen(false);
  }

  return (
    <div className="flex items-center gap-2">
      <div
        role="group"
        aria-label={t("dashboard.period")}
        className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5"
      >
        <SegmentButton
          label={t("dashboard.periodThisWeek")}
          active={filters.preset === "this_week"}
          onClick={() => setPreset("this_week")}
        />
        <SegmentButton
          label={t("dashboard.periodLastWeek")}
          active={filters.preset === "last_week"}
          onClick={() => setPreset("last_week")}
        />
        <Popover open={customOpen} onOpenChange={openCustom}>
          <PopoverTrigger asChild>
            <SegmentButton
              label={t("dashboard.periodCustom")}
              active={filters.preset === "custom"}
            />
          </PopoverTrigger>
          {/* w-80, not w-64: two date fields side by side need the same column width the
              Filters popover gives them, or the last characters of the date clip (POS2-1068). */}
          <PopoverContent align="end" className="w-80 p-4">
            <p className="text-sm font-medium text-gray-900">{t("dashboard.periodCustomRange")}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <InlineDateField
                label={t("dashboard.periodStart")}
                kind="date"
                placeholder={t("dashboard.periodDatePlaceholder")}
                value={from}
                min={EARLIEST_DAY}
                onCommit={(raw) => setFrom(parseDay(raw) ?? "")}
                isValid={(raw) => parseDay(raw) != null}
                testId="period-custom-from"
              />
              <InlineDateField
                label={t("dashboard.periodEnd")}
                kind="date"
                placeholder={t("dashboard.periodDatePlaceholder")}
                value={to}
                min={EARLIEST_DAY}
                onCommit={(raw) => setTo(parseDay(raw) ?? "")}
                isValid={(raw) => parseDay(raw) != null}
                testId="period-custom-to"
              />
            </div>
            {(invertedRange || tooEarly) && (
              <p className="mt-1.5 text-xs text-red-600" data-testid="period-custom-error">
                {invertedRange
                  ? t("dashboard.periodInvalidRange")
                  : t("dashboard.periodTooEarly", { year: EARLIEST_YEAR })}
              </p>
            )}
            <Button
              size="sm"
              className="mt-3 w-full"
              onClick={applyCustom}
              disabled={invertedRange || tooEarly}
              data-testid="period-custom-apply"
            >
              {t("dashboard.periodApply")}
            </Button>
          </PopoverContent>
        </Popover>
      </div>
      <span
        className="hidden items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-600 sm:inline-flex"
        data-testid="period-label"
      >
        <CalendarDays className="size-3.5 text-gray-400" aria-hidden />
        {periodLabel(t, locale, filters, dayRange)}
      </span>
    </div>
  );
}

function SegmentButton({
  label,
  active,
  ...props
}: { label: string; active: boolean } & ComponentProps<"button">) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        "rounded px-2 py-1 text-xs font-medium transition-colors",
        active ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-50 hover:text-gray-700",
      )}
      {...props}
    >
      {label}
    </button>
  );
}

/** Rendered off the window's civil days, so the week shown is the SITE's week (POS2-1039)
 * — `parseISO` of a date-only string is local midnight, i.e. the calendar date itself. */
function periodLabel(
  t: TFunction,
  locale: Locale,
  filters: WasteFilters,
  dayRange: DayRange,
): string {
  const day = (d: string, pattern: string) =>
    format(parseISO(d), pattern, { locale: dateFnsLocale(locale) });
  if ((filters.preset === "this_week" || filters.preset === "last_week") && dayRange.from) {
    // Always the whole Mon–Sun week, even though "this week so far" closes at today.
    return `${day(dayRange.from, "d MMM")} – ${day(weekEndDay(dayRange.from), "d MMM yyyy")}`;
  }
  if (filters.preset === "custom") {
    const from = dayRange.from ? day(dayRange.from, "d MMM yyyy") : t("dashboard.periodStart");
    const to = dayRange.to ? day(dayRange.to, "d MMM yyyy") : t("dashboard.periodNow");
    return `${from} – ${to}`;
  }
  if (filters.preset === "today") return t("dashboard.periodToday");
  if (filters.preset === "all") return t("dashboard.periodAllTime");
  return t("dashboard.periodLastDays", { days: filters.preset.replace("d", "") });
}
